import { useStore, type Peer } from '../../store'
import { Avatar } from '../ui'
import { initialsOf } from '../rail/format'

/**
 * Who else has the console open, at a glance.
 *
 * COLOUR MEANS ROLE, ALWAYS. Techs are blue, dispatchers are pink, and
 * `covering` never changes that — a tech covering a lunch break is still a tech
 * (the standing ruling; see the design doc's §9 note). Coverage is shown as a
 * dot, not a colour, precisely so the two facts stay readable independently.
 *
 * ONLINE MEANS A LIVE CONSOLE, not "covering" and not "recently active". The
 * server counts open `/ops/stream` connections, because the presence beacon
 * only reports while a thread is open — someone reading the rail would
 * otherwise vanish from this bar while plainly sitting there.
 *
 * Renders nothing when nobody else is on. An empty rail of chips reads as
 * "presence is broken"; absence reads as "you are on your own", which is the
 * truth and takes no space.
 */
export function SignedInPeers() {
  const peers = useStore((s) => s.peers)
  const me = useStore((s) => s.me)
  const myRole = useStore((s) => s.role)
  const myCovering = useStore((s) => s.covering)

  // listPeers returns EVERY staff row, the caller included — it only blanks the
  // caller's own typing/reading snapshot, not their entry. Appending "me" on
  // top of that produced two identical chips. Keyed by id so the roster is
  // deduplicated by construction rather than by remembering this again.
  const byId = new Map<string, Peer>()
  for (const p of peers) {
    if (p.online) byId.set(p.id, p)
  }
  if (me) {
    // Local state is the freshest source for a role or coverage flip made a
    // moment ago, and it also covers the first render before presence arrives.
    byId.set(me.id, {
      ...(byId.get(me.id) ?? { id: me.id, typingThreadId: null, readingThreadId: null }),
      id: me.id,
      name: me.name,
      role: myRole,
      covering: myCovering,
      online: true,
      // You are looking at this chip, so you are here. The server's view of
      // you is what everybody else sees; this is only the local render.
      active: true,
    } as Peer)
  }

  const everyone = [...byId.values()]
  if (everyone.length === 0) return null

  return (
    <div
      data-testid="signed-in-peers"
      // Hidden on a phone. Who else is signed in is floor awareness — useful
      // at a desk beside the people it names, and on a phone it is three
      // avatars' worth of the width the header does not have.
      className="hidden items-center gap-1.5 md:flex"
      // One label for the group: a screen reader announcing eight separate
      // avatars is noise, and the useful fact is the roster.
      role="group"
      aria-label={`Signed in: ${everyone.map((p) => p.name).join(', ')}`}
    >
      {everyone.map((p) => (
        // AWAY IS SHOWN, NOT HIDDEN — Slack's model. Dropping away peers out of
        // the roster would make the team look smaller than it is; dimming says
        // "connected, not at the machine", which is the fact a dispatcher
        // actually needs before handing something over.
        //
        // Only an explicit `false` dims. An ops build too old to report
        // activity sends nothing, and greying out the whole team over a version
        // skew would be worse than saying nothing.
        <span
          key={p.id}
          data-testid={`peer-${p.id}`}
          className={['relative inline-flex', p.active === false ? 'opacity-40' : ''].join(' ')}
        >
          <Avatar
            initials={initialsOf(p.name)}
            role={p.role}
            // Fill is reserved for activity elsewhere in the console; here
            // everyone shown is by definition present, so all are outlined and
            // the chips stay visually quiet in a busy header.
            active={false}
            // docs/design-handoff/tossker-redesign/README.md §1 "Header": 26px.
            size={26}
            title={`${p.name} · ${p.role === 'dispatch' ? 'Dispatch' : 'Tech'}${p.covering ? ' · covering' : ''}${
              p.active === false ? ' · away' : ''
            }${p.id === me?.id ? ' · you' : ''}`}
          />
          {p.covering ? (
            <span
              aria-hidden="true"
              data-testid={`peer-covering-${p.id}`}
              // A small dot rather than a colour change, so "who is at the
              // desk" never competes with "what is this person".
              className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-sidebar bg-ok"
            />
          ) : null}
        </span>
      ))}
    </div>
  )
}
